import React, { useCallback, useEffect, useState } from "react";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CalendarDays } from "lucide-react";
import { fmtUsd } from "./FinanceLayout";
import { DEBT_CARD, canEditCard } from "@/lib/placement";

const tone = (b) => b.category === "operating_overhead" ? "text-teal-300" : b.exhibit === "A" ? "text-sky-300" : "text-amber-300";
const tag = (b) => b.category === "operating_overhead" ? "Overhead" : `Exhibit ${b.exhibit}`;

export const ReimburseButton = ({ bill, onDone }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!canEditCard(user, DEBT_CARD) || !["debt_service", "operating_overhead"].includes(bill.category)) return null;
  if (bill.reimbursement) return <span className="mono-body text-[10px] text-teal-300 mr-2" data-testid={`reimbursed-${bill.id}`}>Reimbursed from ...1403 · {bill.reimbursement.date}</span>;
  const go = async () => {
    const day = window.prompt(`The ${bill.vendor_name} draft (${fmtUsd(bill.amount)}) hit Citizens instead of TEA-First Reliance?\nDate it hit Citizens (YYYY-MM-DD):`, bill.due_date);
    if (!day) return;
    setBusy(true);
    try { await apiClient.post("/fin/recurring/reimburse", { bill_id: bill.id, date: day }); toast.success(`Operating reimbursement recorded · ${fmtUsd(bill.amount)} moved ...1403 → Citizens`); onDone?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <button type="button" disabled={busy} onClick={go} className="tick-btn-secondary text-[10px] !py-1.5 !px-2 mr-2" data-testid={`reimburse-${bill.id}`} title="A legacy draft hit Citizens — cover it with a matching transfer from TEA-First Reliance">
      Record as Operating Reimbursement
    </button>
  );
};

export const OutflowCalendar = ({ reloadKey }) => {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const sa = canEditCard(user, DEBT_CARD);
  const load = useCallback(() => apiClient.get("/fin/recurring/calendar", { params: { month: "2026-11" } }).then((r) => setC(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { if (sa) load(); }, [sa, load, reloadKey]);
  if (!sa || !c) return null;
  const t = c.totals;
  return (
    <div className="card-industrial p-5 mb-6" data-testid="outflow-calendar">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 font-semibold"><CalendarDays size={16} className="text-nex-gold" /> November 2026 cash outflow calendar · TEA-First Reliance ...1403</div>
        <div className="text-xs text-nex-muted" data-testid="outflow-totals">
          Debt service {fmtUsd(t.debt_service)} (A {fmtUsd(t.exhibit_a)} · B {fmtUsd(t.exhibit_b)}) · Operating overhead {fmtUsd(t.operating_overhead)} · <span className="text-nex-silver font-semibold">Total {fmtUsd(t.all)}</span>
        </div>
      </div>
      <div className="divide-y divide-nex-border/40 border border-nex-border/40">
        {c.days.map((d) => (
          <div key={d.date} className="grid grid-cols-[88px_1fr_auto] gap-3 px-3 py-2 text-xs" data-testid={`outflow-day-${d.date}`}>
            <span className="mono-body text-nex-chrome">{new Date(`${d.date}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
            <div className="space-y-0.5">
              {d.items.map((b) => (
                <div key={b.id} className="flex flex-wrap gap-x-2">
                  <span className={`mono-label text-[9px] ${tone(b)}`}>{tag(b)}</span>
                  <span>{b.vendor_name}</span>
                  <span className="text-nex-muted">{fmtUsd(b.amount)}{b.autopay ? " · auto-draft" : ""}{b.status === "paid" ? " · paid" : ""}</span>
                </div>
              ))}
            </div>
            <span className="text-right tabular-nums">{fmtUsd(d.total)}<span className="block text-nex-muted">{fmtUsd(d.cumulative)}</span></span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-nex-muted mt-2">Not on this schedule: {c.excluded.join(", ")}.</p>
    </div>
  );
};
